import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import MainButton from "../../components/button/MainButton";

import useIsDesktop from "../../hooks/useIsDesktop";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";

import Pagination from "../../components/pagination/Pagination";
import ConfirmModal from "../../components/modal/ConfirmModal";
import Toast from "../../components/toast/Toast";


import UserRow from "./components/UserRow";
import InviteUserModal from "./components/InviteUserModal";

import UserService from "../../services/userService";
import { getDateString } from "../../utils/formatters";
import { getErrorToastMessage } from "../../utils/httpToast";
import { PAGE_SIZE, LOAD_MORE } from "../../config/constants";

const UserPage = () => {
  const { t } = useTranslation();
  const userService = UserService();

  const isDesktop = useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const initialPageFromUrl = (() => {
    const p = Number(searchParams.get("page"));
    return Number.isFinite(p) && p >= 1 ? p : 1;
  })();

  const [page, setPage] = useState(initialPageFromUrl);

  const initialVisibleCount = (() => {
    const v = Number(searchParams.get("visible"));
    return Number.isFinite(v) && v >= PAGE_SIZE ? v : PAGE_SIZE;
  })();

  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const isMountedRef = useRef(false);

  const scrollYFromUrl = (() => {
    const v = Number(searchParams.get("scrollY"));
    return Number.isFinite(v) && v > 0 ? v : 0;
  })();

  const scrollRestoredRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [serverTotalPages, setServerTotalPages] = useState(null);
  const [totalElements, setTotalElements] = useState(0);

  const [reloadKey, setReloadKey] = useState(0);

  const [toast, setToast] = useState({ open: false, type: "success", message: "" });
  const showToast = (type, message) => {
    setToast({ open: true, type, message });
  };

  useEffect(() => {
    const flashToast = location.state?.toast;
    if (!flashToast) return;

    setToast({
      open: true,
      type: flashToast.type,
      message: flashToast.message,
    });

    navigate(`${location.pathname}${location.search || ""}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (isDesktop) {
      const p = Number(searchParams.get("page"));
      setPage(Number.isFinite(p) && p >= 1 ? p : 1);
    }
  }, [isDesktop, searchParams]);

  useEffect(() => {
    if (!isDesktop) {
      if (!isMountedRef.current) {
        isMountedRef.current = true;
        return;
      }
      setVisibleCount(PAGE_SIZE);
      const params = new URLSearchParams(searchParams);
      params.delete("visible");
      setSearchParams(params, { replace: true });
    }
  }, [isDesktop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const controller = new AbortController();

    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const apiPage = isDesktop ? (page - 1) : 0;
        const apiSize = isDesktop ? PAGE_SIZE : visibleCount;

        const data = await userService.listUsers({ page: apiPage, pageSize: apiSize, signal: controller.signal });

        const mapped = (data?.users ?? []).map((u) => ({
          id: u.id,
          email: u.email,
          createdAt: u.createdDate ? getDateString(u.createdDate, false) : "-",
          role: u.role,
        }));

        setUsers(mapped);
        setServerTotalPages(Math.max(1, Number(data?.totalPages ?? 1)));
        setTotalElements(Number(data?.totalElements ?? 0));
      } catch (err) {
        if (err.name === 'CanceledError') return;
        showToast("error", getErrorToastMessage(err, t));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
    return () => controller.abort();
  }, [isDesktop, page, visibleCount, reloadKey, t]);

  useEffect(() => {
    if (!isDesktop) return;
    if (serverTotalPages === null) return;
    setPage((p) => Math.min(p, serverTotalPages));
  }, [serverTotalPages, isDesktop]);

  const displayedUsers = useMemo(() => users, [users]);

  const hasMoreMobile = !isDesktop && visibleCount < totalElements;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + LOAD_MORE);
  }, []);

  const { sentinelRef, resetLoading } = useInfiniteScroll({
    enabled: !isDesktop && !isLoading,
    hasMore: hasMoreMobile,
    onLoadMore: handleLoadMore,
  });

  useEffect(() => {
    resetLoading();
  }, [visibleCount, resetLoading]);

  useEffect(() => {
    if (isDesktop) return;
    if (visibleCount === PAGE_SIZE) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("visible", String(visibleCount));
      return params;
    }, { replace: true });
  }, [visibleCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading) return;
    if (scrollRestoredRef.current) return;
    if (scrollYFromUrl <= 0) return;
    scrollRestoredRef.current = true;
    requestAnimationFrame(() => window.scrollTo({ top: scrollYFromUrl, behavior: "instant" }));
  }, [isLoading, scrollYFromUrl]);

  const onView = (user) => {
    const scrollY = window.scrollY;
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("scrollY");
    const search = currentParams.toString() ? `?${currentParams.toString()}` : "";
    const base = `${location.pathname}${search}`;
    const sep = base.includes("?") ? "&" : "?";
    const returnUrl = encodeURIComponent(`${base}${sep}scrollY=${scrollY}`);
    navigate(`/users/${user.id}?returnUrl=${returnUrl}`, { state: { user } });
  };

  const onInvite = async (email, role = "USER") => {
    try {
      await userService.inviteUser({ email, role });
      showToast("success", t("MODAL.SUCCESSFUL"));
      setReloadKey((k) => k + 1);
    } catch (err) {
      showToast("error", getErrorToastMessage(err, t));
    }
  };

  const onDelete = (user) => {
    setUserToDelete(user);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await userService.deleteUser(userToDelete.id);
      showToast("success", t("MODAL.SUCCESSFUL"));
      setReloadKey((k) => k + 1);
    } catch (err) {
      showToast("error", getErrorToastMessage(err, t));
    } finally {
      setUserToDelete(null);
    }
  };

  return (
    <div className="w-full bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-6 py-14">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t("NAVBAR.USERS")}</h1>

          <MainButton
            variant="secondary"
            className="px-4 py-1.5 text-xs md:text-sm flex items-center gap-2 rounded-xl"
            onClick={() => setShowInviteModal(true)}
          >
            <span className="text-base leading-none">+</span>
            <span>{t("USERS.INVITE")}</span>
          </MainButton>
        </div>

        <ul className="space-y-3">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <UserRow key={`skeleton-${index}`} isLoading={true} />
            ))
          ) : (
            displayedUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                onView={onView}
                onDelete={onDelete}
              />
            ))
          )}
        </ul>

        {!isDesktop && (
          <>
            <div ref={sentinelRef} className="h-10" />
            {hasMoreMobile && !isLoading && (
              <p className="mt-2 text-center text-sm text-mediumGray">
                {t("ACTIONS.LOADING_MORE")}
              </p>
            )}
          </>
        )}

        {isDesktop && (serverTotalPages ?? 0) > 1 && !isLoading && (
          <Pagination
            page={page}
            totalPages={serverTotalPages ?? 1}
            onChange={(p) => {
              setSearchParams({ page: String(p) }, { replace: true });
              setPage(p);
            }}
            maxButtons={5}
          />
        )}
      </div>

      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={onInvite}
      />

      <ConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={confirmDelete}
        title={t("MODAL.REMOVE")}
        description={t("MODAL.SAFE_REMOVE_USER")}
        cancelLabel={t("ACTIONS.CANCEL")}
        confirmLabel={t("MODAL.REMOVE")}
        confirmClassName="bg-red text-white hover:opacity-90"
      />

      <Toast
        open={toast.open}
        variant={toast.type}
        message={toast.message}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default UserPage;